/**
 * useStudentFinishedActivityView — une activité finie, rouverte, prête à poser : le moteur
 * (`useFinishedActivity` : la copie relue, la correction marquée lue, l'avis, « Refaire »), la
 * figure de l'énoncé, le lecteur de l'enregistrement d'une activité orale, le rendu ouvert ou non,
 * et ce qu'ils donnent en props de `<StudentFinishedActivityPage>`.
 *
 * Le rendu ouvert se referme par sa croix ou par le retour d'Android, comme l'ancienne boîte ; sinon
 * le retour quitte l'écran. « Refaire l'activité » pose la nouvelle activité « en cours » : l'écran
 * de la route passe alors à son exécution (`StudentActivityScreen`).
 */
import { useIsFocused, useNavigation } from "@react-navigation/native"
import { useEffect, useMemo, useRef, useState } from "react"
import { BackHandler } from "react-native"
import { useSelector } from "react-redux"

import { type StudentFinishedActivityPageProps } from "../../../../../../design-system/pages/StudentFinishedActivityPage"
import { cleanMediaUrl } from "../../../../../../data/constants/apiConstants"
import { useFinishedActivity } from "../../../../../../engine/activity/result/useFinishedActivity"
import { useStatementFigure } from "../../../../../../engine/activity/useStatementFigure"
import {
    type ApiChallenge,
    type ApiFinishedActivity,
    type ApiFinishedTemplate,
} from "../../../../adapters/student/Activity/finishedActivityAdapter"
import {
    toStudentActivityStatement,
    type ApiRunActivity,
} from "../../../../adapters/student/Activity/studentActivityAdapter"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    correctionFeedbackError,
    finishedActivityModel,
    finishedActivityNotice,
} from "../../../../view-models/student/Activity/finishedActivityViewModel"
import { type UIConversationMessage } from "../../../types/studentConversation"
import { useConversationVoicePlayer } from "../Messaging/useConversationVoicePlayer"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type FinishedRoot = {
    data?: {
        staticData?: { activity_templates?: ApiFinishedTemplate[]; school_subjects?: ApiSchoolSubject[] }
        currentUser?: {
            object?: { challenges?: ApiChallenge[]; history?: { challenges?: ApiChallenge[] } } | null
        }
    }
}

const clean = (url: string | null | undefined) => (cleanMediaUrl(url) as string | null | undefined) ?? null

/**
 * `inline` : dans le volet du live, sous le seul en-tête du live. Pas de retour (l'élève ne quitte pas
 * son live). « Refaire l'activité » n'y est offert que si le live sait ouvrir l'activité refaite
 * (`onReplayed`) : l'ancien la relançait dans la séance et prévenait le tuteur (`newTodo`), ce que
 * seul l'écran du live peut faire.
 *
 * `onBack` : le retour est celui de l'appelant, pas celui de la pile — la relecture d'une activité
 * faite dans le volet du live, qui rend l'activité du live. Son titre se lit alors dans la barre, avec
 * la flèche, et le retour d'Android la referme aussi.
 */
export function useStudentFinishedActivityView(
    activity: ApiFinishedActivity,
    options: {
        inline?: boolean
        /** Le rendu ouvert d'emblée (la copie corrigée), à chaque activité ouverte. */
        openWork?: boolean
        onBack?: () => void
        /** L'activité refaite, à ouvrir là où l'on est (le live). Hors live, l'écran de la route suit seul. */
        onReplayed?: (fresh: { id?: number | null } & Record<string, unknown>) => void
    } = {}
): StudentFinishedActivityPageProps {
    const inline = options.inline === true
    const close = options.onBack
    const navigation = useNavigation()
    const focused = useIsFocused()
    const templates = useSelector((state: FinishedRoot) => state.data?.staticData?.activity_templates)
    const subjects = useSelector((state: FinishedRoot) => state.data?.staticData?.school_subjects)
    const challenges = useSelector((state: FinishedRoot) => selectDisplayedStudent(state)?.challenges)
    const pastChallenges = useSelector((state: FinishedRoot) => selectDisplayedStudent(state)?.history?.challenges)

    const openWorkAtStart = options.openWork === true
    const [workOpen, setWorkOpen] = useState(openWorkAtStart)
    const activityId = activity.id ?? null
    // À chaque activité ouverte, le rendu repart comme demandé ; le refermer ensuite tient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setWorkOpen(openWorkAtStart), [activityId])

    const engine = useFinishedActivity({ activity, active: focused, workOpen, onRedone: options.onReplayed })

    const figure = useStatementFigure(activity.material ?? null)
    const statement = useMemo(
        () =>
            toStudentActivityStatement({
                ...(activity as ApiRunActivity),
                material: figure.material ?? activity.material ?? null,
            }),
        [activity, figure.material]
    )
    const model = useMemo(
        () =>
            finishedActivityModel({
                activity,
                correction: engine.correction,
                statement,
                templates,
                subjects,
                challenges: [...(challenges ?? []), ...(pastChallenges ?? [])],
                cleanUrl: clean,
            }),
        [activity, engine.correction, statement, templates, subjects, challenges, pastChallenges]
    )

    // L'enregistrement d'une activité orale se joue dans le rendu ; le refermer l'arrête.
    const audioUri = model.details.work?.audioUri ?? null
    const player = useConversationVoicePlayer(workOpen && audioUri != null ? `work-${activityId}` : null)
    const recording = useMemo<UIConversationMessage | null>(
        () =>
            audioUri != null
                ? {
                      id: `work-${activityId}`,
                      author: "self",
                      attachment: { kind: "voice", uri: audioUri, durationLabel: "" },
                  }
                : null,
        [audioUri, activityId]
    )

    // Le retour d'Android referme le rendu ouvert, comme il refermait l'ancienne boîte ; puis, quand
    // l'appelant tient le retour, la relecture.
    const workOpenRef = useRef(workOpen)
    workOpenRef.current = workOpen
    const closeRef = useRef(close)
    closeRef.current = close
    useEffect(() => {
        const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
            if (!navigation.isFocused()) return false
            if (workOpenRef.current) {
                setWorkOpen(false)
                return true
            }
            if (closeRef.current == null) return false
            closeRef.current()
            return true
        })
        return () => subscription.remove()
    }, [navigation])

    return {
        activity: model.details,
        figurePending: figure.figurePending,
        heading: inline && close == null ? "inline" : "bar",
        onBack: close ?? (inline ? undefined : () => navigation.goBack()),
        medium: model.medium,
        correction: model.correction,
        workLoading: engine.state.loading,
        onOpenWork: () => setWorkOpen(true),
        workOpen,
        onCloseWork: () => setWorkOpen(false),
        comment: model.comment,
        feedback: engine.state.feedback,
        onFeedback: engine.submitFeedback,
        feedbackSubmitting: engine.state.feedbackSubmitting,
        feedbackError: correctionFeedbackError(engine.state.feedbackFailure),
        audio:
            recording != null
                ? {
                      playing: player.messageId === recording.id && player.playing,
                      progress: player.messageId === recording.id ? player.progress : 0,
                      onTogglePlay: () => player.toggle(recording),
                  }
                : undefined,
        onRedo:
            model.redo.offered && (!inline || options.onReplayed != null)
                ? () => engine.redo(model.redo.body)
                : undefined,
        redoing: engine.state.redoing,
        notice: finishedActivityNotice(engine.state.notice),
        onDismissNotice: engine.dismissNotice,
    }
}
